/**
 * Schedule health across the fleet — the page behind the dashboard's "Sites
 * With Games" tile. Every active site, grouped by whether its league schedule
 * actually has games coming, problems first.
 *
 * A source that returns nothing is a successful sync, so "connected but empty"
 * was invisible until this page: off-season, a feed pointed at the wrong place
 * and a forgotten setup all look the same from inside a sync log.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CalendarCheck, ChevronLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { schedulesApi, queryKeys, type ScheduleHealthSite, type ScheduleHealthGroup } from '@/lib/api';

const SOURCE_LABEL: Record<string, string> = {
  google_sheets: 'Google Sheet',
  ccm_api: 'CurlingClubManager',
  leagueapps_api: 'LeagueApps',
  curlops_api: 'CurlOps',
  generic_recurring: 'Recurring template',
  manual: 'Manual',
};

const GROUPS: Array<{ key: ScheduleHealthGroup; title: string; blurb: string; tone: string }> = [
  { key: 'sync_problem', title: 'Sync problem', tone: 'border-red-300 dark:border-red-800',
    blurb: 'The schedule source is not configured, has stopped syncing, or reported rows it could not import.' },
  { key: 'has_games', title: 'Has games', tone: 'border-green-300 dark:border-green-800',
    blurb: 'League games in the next 7 days.' },
  { key: 'connected_empty', title: 'Connected, no games', tone: 'border-amber-300 dark:border-amber-800',
    blurb: 'The source syncs fine but has nothing in the next 7 days — off-season, or a feed pointed at the wrong place.' },
  { key: 'manual_empty', title: 'Manual, nothing entered', tone: 'border-gray-300 dark:border-gray-700',
    blurb: 'Schedule is entered by hand in CSM, and nothing is entered for the next 7 days.' },
  { key: 'not_set_up', title: 'Not set up', tone: 'border-gray-200 dark:border-gray-800',
    blurb: 'No rink host and no games — probably not onboarded yet.' },
];

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}

function siteDateTime(iso: string, tz: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return new Date(iso).toLocaleString(); }
}

function siteDate(iso: string, tz: string): string {
  try { return new Date(iso).toLocaleDateString('en-US', { timeZone: tz, month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return new Date(iso).toLocaleDateString(); }
}

function SiteRow({ s }: { s: ScheduleHealthSite }) {
  return (
    <tr className="border-t border-gray-100 text-gray-700 first:border-t-0 dark:border-gray-800 dark:text-gray-300">
      <td className="whitespace-nowrap px-4 py-2 font-medium text-gray-900 dark:text-gray-100">
        {s.name} <span className="ml-1 text-xs font-normal text-gray-400 dark:text-gray-500">{s.facilityId}</span>
      </td>
      <td className="whitespace-nowrap px-2 py-2 text-xs">{SOURCE_LABEL[s.scheduleSource] ?? s.scheduleSource}</td>
      <td className="whitespace-nowrap px-2 py-2 text-xs text-gray-500 dark:text-gray-400">
        {s.automatic ? `synced ${ago(s.lastSync)}` : '—'}
      </td>
      <td className="whitespace-nowrap px-2 py-2 text-right text-xs">
        <span className="font-semibold text-gray-900 dark:text-gray-100">{s.gamesNext7Days}</span> next 7 days
        {s.gamesToday > 0 && <span className="text-gray-500 dark:text-gray-400"> · {s.gamesToday} today</span>}
      </td>
      <td className="px-2 py-2 text-xs">
        {s.problem
          ? <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400"><AlertCircle className="h-3 w-3" />{s.problem}</span>
          : s.nextGame
            ? <span>Next: {siteDateTime(s.nextGame.at, s.timezone)} · sheet {s.nextGame.sheetId} · <span className="text-gray-500 dark:text-gray-400">{s.nextGame.eventName}</span></span>
            : <span className="text-gray-400 dark:text-gray-500">No upcoming games</span>}
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-right text-xs text-gray-500 dark:text-gray-400">
        {s.lastGame ? `last game ${siteDate(s.lastGame.at, s.timezone)}` : 'no games yet'}
      </td>
    </tr>
  );
}

export default function ScheduleHealthPage() {
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: queryKeys.schedules.scheduleHealth(),
    queryFn: () => schedulesApi.getScheduleHealth(),
    refetchInterval: 60_000,
  });

  return (
    <div className="space-y-5 p-6">
      <div>
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-gray-100">
          <ChevronLeft className="h-4 w-4" /> Dashboard
        </Link>
        <div className="mt-2 flex items-center gap-3">
          <span className="rounded-lg bg-emerald-50 p-2.5 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
            <CalendarCheck className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Schedules</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {data
                ? `${data.counts.hasGames} of ${data.counts.total} sites have league games in the next ${data.windowDays} days`
                : 'Loading…'}
              {dataUpdatedAt > 0 && <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">updated {new Date(dataUpdatedAt).toLocaleTimeString()}</span>}
            </p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle className="h-4 w-4" /> Could not load schedule health
        </div>
      ) : (
        GROUPS.map(g => {
          const sites = (data?.sites ?? []).filter(s => s.group === g.key);
          if (sites.length === 0) return null;
          return (
            <section key={g.key} className={`overflow-hidden rounded-lg border bg-white dark:bg-gray-900 ${g.tone}`}>
              <div className="border-b border-gray-100 px-4 py-2.5 dark:border-gray-800">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {g.title} <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">{sites.length}</span>
                </h2>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{g.blurb}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm"><tbody>{sites.map(s => <SiteRow key={s.facilityId} s={s} />)}</tbody></table>
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
